import type FullCalendar from "@fullcalendar/react"
import { EventContentArg } from "@fullcalendar/core/index.js"
import { useEffect, useState } from "react"
import { createPortal, flushSync } from "react-dom"

import { SagecoolCalendar } from "./sagecool-calendar"

export function FullCalendarWithEventPortals({
  calendarRef,
  mirrorEventContent,
  portalEventContent,
  ...calendarProps
}: {
  calendarRef: (calendar: FullCalendar | null) => void
  mirrorEventContent: (eventContentArg: EventContentArg) => React.ReactNode
  portalEventContent: (eventContentArg: EventContentArg) => React.ReactNode
} & Omit<
  React.ComponentProps<typeof FullCalendar>,
  "ref" | "eventContent" | "windowResize"
>) {
  const [eventNodes, setEventNodes] = useState<
    Array<[HTMLDivElement, EventContentArg]>
  >([])
  const [pendingEventNodes] = useState<Map<HTMLDivElement, EventContentArg>>(
    new Map(),
  )

  useEffect(() => {
    queueMicrotask(() => {
      if (pendingEventNodes.size > 0) {
        flushSync(() => setEventNodes([...pendingEventNodes.entries()]))
        pendingEventNodes.clear()
      }
    })
  })

  return (
    <>
      <SagecoolCalendar
        ref={calendarRef}
        windowResize={() => flushSync(() => setEventNodes([...eventNodes]))}
        eventContent={(eventContentArg) => {
          if (eventContentArg.isMirror) {
            return mirrorEventContent(eventContentArg)
          }

          return (
            <div
              className="contents"
              ref={(node) => {
                if (node) pendingEventNodes.set(node, eventContentArg)
              }}
            ></div>
          )
        }}
        {...calendarProps}
      />
      {eventNodes.map(([node, eventContentArg]) =>
        createPortal(portalEventContent(eventContentArg), node),
      )}
    </>
  )
}
